<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  CloseIcon,
  CopyIcon,
  DangerTriangleIcon,
  MaximizeSquareIcon,
  PenIcon,
  SquareArrowRightUpIcon,
  TrashBinTrashIcon,
} from '@solar-icons/vue/linear'
import { CloseCircleIcon } from '@solar-icons/vue/bold'
import { LinkedinIcon } from '@/components/primitives/brand-icons'
import AppBadge from '../../primitives/AppBadge/AppBadge.vue'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppDateTimePicker from '../../primitives/AppDateTimePicker/AppDateTimePicker.vue'
import AppDockPanel from '../../primitives/AppDockPanel/AppDockPanel.vue'
import AppScrollArea from '../../primitives/AppScrollArea/AppScrollArea.vue'
import AppNotice from '../../primitives/AppNotice/AppNotice.vue'
import type { BadgeTone } from '../../primitives/AppBadge/badge-types'
import AppSeparator from '../../primitives/AppSeparator/AppSeparator.vue'
import AppPageSection from '../../shell/AppPage/AppPageSection.vue'
import PostResultsPanel from '../PostResultsPanel/PostResultsPanel.vue'
import PostMediaSection from '../PostMediaSection/PostMediaSection.vue'
import PostRevisionHistory from '../PostRevisionHistory/PostRevisionHistory.vue'
import ArticleDockSummary from '../ArticleDockSummary/ArticleDockSummary.vue'
import AppContextChips from '../../chat/AppChatPanel/AppContextChips.vue'
import type { ContextItem } from '../../chat/AppChatPanel/context-types'
import AppPromptInput from '../../chat/AppPromptInput/AppPromptInput.vue'
import { BELIEF_PHASE_ACCENT } from '../../brand/BeliefMapBoard/belief-phase'
import { PHASE_LABEL_KEYS } from '../../../lib/strategy/journey-phase'
import { PLATFORM_LABELS } from '../ScheduleItem/schedule-item-types'
import {
  STATUS_BADGE,
  TRANSITION_CONTROL,
  type ContentTransition,
} from '../../../lib/content/content-status'
import { postMediaState } from '../../../lib/content/post-media'
import {
  TRANSITION_ACTION,
  pieceActionLabelKey,
  pieceActionsFor,
  type PieceActionSubject,
} from '../../../lib/content/piece-actions'
import type { JourneyPhase } from '../../../types/brand'
import type {
  ArticleDoc,
  ContentChannel,
  ContentItem,
  ContentRevision,
  PostResults,
} from '../../../types/content'
import type { PostAction } from '../../../stores/post-panel'
import type { DesignRun } from '../../../types/design'
import { errorCopyKey } from '../../../lib/inbox/error-copy'
import { useFormatters } from '../../../composables/useFormatters'
import { needsPerson } from '../../../types/post-run'

/** What the post argues: the belief's stage and its claim. */
export interface PostArgument {
  readonly phase: JourneyPhase
  readonly claim: string
}

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    /** The post shown in the dock, or `null` when nothing is open (renders nothing). */
    item?: ContentItem | null
    revisions?: ContentRevision[]
    currentRevisionId?: string
    revisionsLoading?: boolean
    /** The post's analytics, or undefined when nothing is recorded yet; only shown once published. */
    results?: PostResults
    /** The belief the post or article argues; absent for a hand-made post. */
    argument?: PostArgument | null
    /** The post's topic name, when it has one. */
    topicName?: string | null
    /** The question of the page an article answers. */
    question?: string | null
    /** The article's document; `null` for a post or an article Raldo has not planned yet. */
    article?: ArticleDoc | null
    /** The article's journey stage when no argument resolves; ignored for a post. */
    phase?: JourneyPhase | null
    /** Raldo is still writing the post: only its argument shows. */
    writing?: boolean
    /** The newest run designing the post's media, or `null` when none ran. */
    designRun?: DesignRun | null
    /** Whether the given action is in flight for this post; drives every button's `loading`. */
    isActionPending?: (action: PostAction) => boolean
  }>(),
  {
    item: null,
    revisions: () => [],
    currentRevisionId: undefined,
    revisionsLoading: false,
    results: undefined,
    argument: null,
    topicName: null,
    question: null,
    article: null,
    phase: null,
    writing: false,
    designRun: null,
    isActionPending: () => false,
  },
)

const emit = defineEmits<{
  close: []
  /** Opens the full write surface: the article workbench or the post page. */
  writePost: [id: string]
  edit: [id: string]
  schedule: [id: string]
  reschedule: [id: string, publishAt: string]
  unschedule: [id: string]
  remove: [id: string]
  publishNow: [id: string]
  /** The composer was submitted; the owner sends it as a chat message with this post pinned. */
  askRaldo: [id: string, message: string]
  duplicate: [id: string]
  retryPublish: [id: string]
  restore: [revisionId: string]
  openDesign: [designId: string]
  /** Starts the design of the post's media from its slides. */
  designMedia: [id: string]
}>()

const CHANNEL_TONE: Record<ContentChannel, BadgeTone> = {
  linkedin: 'linkedin',
}

const { formatDateTime } = useFormatters()

const isArticle = computed(() => props.item?.shape === 'article')

const label = computed(() => {
  if (isArticle.value) return t('content.calendar.articleBadge')
  const date = props.item?.scheduledAt
  if (!date) return t('content.post.unscheduled')
  return formatDateTime(date, 'weekdayMonthDay')
})

const status = computed(() => (props.item ? STATUS_BADGE[props.item.status] : null))

const subject = computed<PieceActionSubject | null>(() =>
  props.item
    ? { shape: props.item.shape, status: props.item.status, writing: props.writing }
    : null,
)

const actions = computed(() => (subject.value ? pieceActionsFor(subject.value) : null))

const openLabel = computed(() =>
  subject.value ? t(pieceActionLabelKey('open', subject.value)) : '',
)

const editLabel = computed(() =>
  subject.value ? t(pieceActionLabelKey('edit', subject.value)) : '',
)

const editedByYou = computed(
  () =>
    props.revisions.find((revision) => revision.id === props.currentRevisionId)?.author === 'user',
)

const check = computed(() =>
  props.item?.score === null || props.item === null
    ? null
    : { score: props.item.score, reasons: props.item.reasons },
)

const canEdit = computed(() => actions.value?.edit === true)
const canDelete = computed(() => actions.value?.remove === true)
const canDuplicate = computed(() => actions.value?.duplicate === true)
const transitions = computed(() => actions.value?.transitions ?? [])

const canDesignMedia = computed(
  () => props.item !== null && postMediaState(props.item, props.designRun).canDesign,
)

const datePickers = ref<InstanceType<typeof AppDateTimePicker>[]>([])

/** Moves focus to the reschedule date control, when the post offers one. */
function focusDate(): void {
  datePickers.value[0]?.focus()
}

defineExpose({ focusDate })

function onReschedule(value: Date) {
  if (!props.item) return
  emit('reschedule', props.item.id, value.toISOString())
}

function onTransition(transition: Exclude<ContentTransition, 'reschedule'>) {
  if (!props.item) return
  const id = props.item.id
  const emitFor: Record<Exclude<ContentTransition, 'reschedule'>, () => void> = {
    schedule: () => emit('schedule', id),
    publishNow: () => emit('publishNow', id),
    unschedule: () => emit('unschedule', id),
    retry: () => emit('retryPublish', id),
  }
  emitFor[transition]()
}

function onWindowKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.defaultPrevented) return
  if (props.item) emit('close')
}

onMounted(() => {
  window.addEventListener('keydown', onWindowKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKeydown)
})

const contextItem = computed<ContextItem | null>(() => {
  if (!props.item) return null
  return {
    id: props.item.id,
    label: props.item.title || t('content.untitledPost'),
    kind: 'post',
    locked: true,
  }
})

const message = ref('')

function onAskSubmit(text: string) {
  if (!props.item) return
  const trimmed = text.trim()
  if (!trimmed) return
  emit('askRaldo', props.item.id, trimmed)
  message.value = ''
}

watch(
  () => props.item?.id ?? null,
  () => {
    message.value = ''
  },
)
</script>

<template>
  <AppDockPanel v-if="item" edge="sheet" :label="label">
    <template #header-actions>
      <AppButton
        size="sm"
        icon-only
        :start-icon="MaximizeSquareIcon"
        variant="ghost"
        :aria-label="openLabel"
        :title="openLabel"
        @click="emit('writePost', item.id)"
      />
      <AppButton
        size="sm"
        icon-only
        :start-icon="CloseIcon"
        variant="ghost"
        :aria-label="t('content.peek.closeDetails')"
        :title="t('content.peek.closeDetails')"
        @click="emit('close')"
      />
    </template>

    <AppScrollArea class="flex min-h-0 flex-1 flex-col gap-12 p-12">
      <ArticleDockSummary
        v-if="isArticle"
        :item="item"
        :article="article"
        :phase="argument?.phase ?? phase"
        :question="question"
        :topic-name="topicName"
        :can-delete="canDelete"
        :removing="isActionPending('remove')"
        @open-article="emit('writePost', item.id)"
        @remove="emit('remove', item.id)"
      />

      <template v-else>
        <div class="flex flex-wrap items-center gap-2">
          <AppBadge v-if="writing" tone="blue" indicator="pulse">
            {{ t('calendar.badge.writing') }}
          </AppBadge>
          <template v-else>
            <AppBadge
              v-for="channel in item.channels"
              :key="channel"
              :tone="CHANNEL_TONE[channel]"
              :icon="LinkedinIcon"
            >
              {{ PLATFORM_LABELS[channel] }}
            </AppBadge>
            <AppBadge v-if="status" :tone="status.tone">{{ t(status.labelKey) }}</AppBadge>
            <AppBadge v-if="editedByYou" tone="quiet">{{ t('calendar.editedByYou') }}</AppBadge>
          </template>
        </div>

        <template v-if="!writing">
          <AppNotice
            v-if="item.status === 'failed' && item.publishingError"
            tone="error"
            :icon="CloseCircleIcon"
            :title="t('calendar.dock.failed')"
            :message="t(errorCopyKey(item.publishingError))"
          />

          <AppNotice
            v-if="check"
            :tone="needsPerson(check.score) ? 'warning' : 'success'"
            :icon="needsPerson(check.score) ? DangerTriangleIcon : undefined"
            :title="t(needsPerson(check.score) ? 'content.post.needsPerson' : 'content.post.ready')"
            :message="t('content.post.score', { score: check.score })"
            :details="check.reasons"
          />

          <PostMediaSection
            :item="item"
            heading-level="h3"
            :design-run="designRun"
            @open-design="(designId) => emit('openDesign', designId)"
          />
        </template>

        <AppPageSection v-if="argument" :title="t('calendar.dock.whatItArgues')" heading-level="h3">
          <AppBadge class="self-start" :tone="BELIEF_PHASE_ACCENT[argument.phase]">
            {{ t(PHASE_LABEL_KEYS[argument.phase]) }}
          </AppBadge>
          <div class="flex flex-col gap-2">
            <p class="text-body-sm text-text-primary">{{ argument.claim }}</p>
            <p v-if="topicName" class="text-caption text-text-tertiary">
              {{ t('calendar.dock.topic', { name: topicName }) }}
            </p>
          </div>
        </AppPageSection>

        <template v-if="!writing">
          <AppPageSection
            v-if="item.status === 'published'"
            :title="t('content.resultsLabel')"
            heading-level="h3"
          >
            <PostResultsPanel :results="results" />
            <a
              v-if="item.publishedUrl"
              :href="item.publishedUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="icon-sm flex w-fit items-center gap-2 text-caption-medium text-accent-blue hover:underline"
            >
              {{ t('content.openOnLinkedIn') }}
              <SquareArrowRightUpIcon class="shrink-0" />
            </a>
          </AppPageSection>

          <AppPageSection :title="t('content.copyLabel')" heading-level="h3">
            <p v-if="item.copy" class="text-body-sm whitespace-pre-wrap text-text-primary">
              {{ item.copy }}
            </p>
            <p v-else class="text-body-sm text-text-tertiary">
              {{ t('content.writePostCopyPlaceholder') }}
            </p>
          </AppPageSection>

          <AppPageSection v-if="item.media" :title="t('content.post.slides')" heading-level="h3">
            <template v-if="canDesignMedia" #actions>
              <AppButton
                size="sm"
                variant="ghost"
                :label="t('content.post.designMedia')"
                :loading="isActionPending('designMedia')"
                @click="emit('designMedia', item.id)"
              />
            </template>
            <ol class="flex flex-col gap-6">
              <li
                v-for="(slide, index) in item.media.units"
                :key="index"
                class="flex flex-col gap-1"
              >
                <span class="text-caption text-text-tertiary">
                  {{ t('content.post.slide', { n: index + 1 }) }} · {{ slide.block }}
                </span>
                <p
                  v-for="component in slide.components"
                  :key="component.type"
                  class="text-body-sm whitespace-pre-wrap text-text-primary"
                >
                  {{ component.text }}
                </p>
              </li>
            </ol>
          </AppPageSection>

          <PostRevisionHistory
            :revisions="revisions"
            :current-revision-id="currentRevisionId"
            :loading="revisionsLoading"
            @restore="(revisionId) => emit('restore', revisionId)"
          />

          <AppSeparator />

          <div class="flex flex-wrap items-center gap-4">
            <template v-for="transition in transitions" :key="transition">
              <AppDateTimePicker
                v-if="transition === 'reschedule'"
                ref="datePickers"
                :model-value="null"
                :placeholder="t(TRANSITION_CONTROL.reschedule.labelKey)"
                :aria-label="t(TRANSITION_CONTROL.reschedule.labelKey)"
                @update:model-value="onReschedule"
              />
              <AppButton
                v-else
                :variant="TRANSITION_CONTROL[transition].variant"
                :label="t(TRANSITION_CONTROL[transition].labelKey)"
                :loading="isActionPending(TRANSITION_ACTION[transition])"
                @click="onTransition(transition)"
              />
            </template>
            <AppButton
              v-if="canDuplicate"
              variant="primary"
              :label="t('content.duplicate')"
              :start-icon="CopyIcon"
              :loading="isActionPending('duplicate')"
              @click="emit('duplicate', item.id)"
            />

            <div class="ml-auto flex items-center gap-2">
              <AppButton
                v-if="canEdit"
                icon-only
                :start-icon="PenIcon"
                variant="ghost"
                :aria-label="
                  t('content.actionForTitleAria', { action: editLabel, title: item.title })
                "
                :title="editLabel"
                @click="emit('edit', item.id)"
              />
              <AppButton
                v-if="canDelete"
                icon-only
                :start-icon="TrashBinTrashIcon"
                variant="ghost"
                :loading="isActionPending('remove')"
                :aria-label="
                  t('content.actionForTitleAria', { action: t('common.delete'), title: item.title })
                "
                :title="t('common.delete')"
                @click="emit('remove', item.id)"
              />
            </div>
          </div>
        </template>
      </template>
    </AppScrollArea>

    <template v-if="isArticle || !writing" #footer>
      <AppPromptInput
        v-model="message"
        :attach="false"
        :has-context="contextItem !== null"
        :placeholder="
          isArticle ? t('calendar.dock.askArticlePlaceholder') : t('content.post.askPlaceholder')
        "
        @submit="onAskSubmit"
      >
        <template v-if="contextItem" #context>
          <AppContextChips :items="[contextItem]" />
        </template>
      </AppPromptInput>
    </template>
  </AppDockPanel>
</template>
